<template>
  <div class="configure-export-view">
    <modal
      :active="true"
      :primary-button-disabled="isLoading"
      :primary-button-loading="isLoading"
      delete-button-label="Restore Defaults"
      tertiary-button-label="Cancel"
      primary-button-label="Apply"
      size="small"
      @onDelete="restoreDefaults()"
      @onClose="onClose()"
      @onConfirm="saveColumnConfig()">
      <template #header>
        <p>Configure columns to export</p>
      </template>
      <template #content>
        <div class="configure-export-view__modal">
          <div class="configure-export-view__modal__body">
            <div class="configure-export-view__modal__body__left">
              <div class="configure-export-view__modal__body__left__header">
                Default Columns
              </div>
              <div
                v-for="(item, key) in configuration"
                :key="key"
                class="configure-export-view__modal__body__left__title">
                <div
                  v-if="item.isDefault"
                  v-tooltip="key"
                  class="configure-export-view__modal__body__left__title__tab truncate">
                  {{ key }}
                </div>
              </div>
            </div>
            <div class="configure-export-view__modal__body__right">
              <div class="configure-export-view__modal__body__right__header">
                Available Columns
              </div>
              <div class="configure-export-view__modal__body__right__search">
                <text-input
                  id="export-search-input"
                  placeholder="Search for Column"
                  :value="searchTerm"
                  :show-clear-button="true"
                  icon="magnifying-glass"
                  @onChange="onSearch($event)"
                  @onClear="onClear" />
              </div>
              <div class="configure-export-view__modal__body__right__title">
                <div v-if="filteredCurrentConfigColumnsIndex.length">
                  <div
                    v-for="item in filteredCurrentConfigColumnsIndex"
                    :key="item"
                    class="configure-export-view__modal__body__right__title__column">
                    <div
                      class="configure-export-view__modal__body__right__title__column__title">
                      <span
                        class="configure-export-view__modal__body__right__title__column__title__value">
                        {{ item }}
                      </span>
                    </div>
                    <div
                      class="configure-export-view__modal__body__right__title__column__icons">
                      <span
                        v-if="currentConfigColumns[item].isVisible"
                        class="mi-24px mi-eye"
                        aria-hidden="true"
                        @click="
                          toggleColumnVisibility(currentConfigColumns[item])
                        "></span>
                      <span
                        v-else
                        class="mi-24px mi-eye-slash"
                        aria-hidden="true"
                        @click="
                          toggleColumnVisibility(currentConfigColumns[item])
                        "></span>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="configure-export-view__modal__body__right__title__no-items-found">
                  <span>No Items found, please use correct column names.</span>
                </div>
              </div>
            </div>
          </div>
          <div class="configure-export-view__modal__notification">
            <notification
              icon="info-circle"
              type="info"
              title="Note: "
              classes="custom-toast">
              <div>Maximum 5K records can be exported.</div>
            </notification>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import Vue from "vue";
import xlsx from "json-as-xlsx";
import { format } from "date-fns";
import { showToast } from "@/components/common/Toast";
import { disputeActions } from "@/actions";
import AxiosFetch from "@/utils/axios";

// Helpers
import {
  filterGroupFlatten,
  getMsalIdToken,
  getCaseAging,
  // getRejectVoiceCaseAging,
  // getInprocessCaseAging,
} from "@/utils/helpers.js";

export default {
  name: "ExportExcelModal",
  props: {
    isOpen: { type: Boolean, default: false },
    configuration: { type: Object, required: true },
    defaultColumns: { type: Array, required: true },
    cacheName: { type: String, default: "" },
    pagination: { type: Object, required: true },
    filterData: { type: Object, required: true },
    sortOrderConfiguration: { type: Object, required: true },
    recordsCount: { type: Number, required: true },
    appliedSearchTerm: { type: String, default: "" },
  },
  data() {
    return {
      configColumns: [],
      searchTerm: "",
      currentConfigColumns: {},
      filteredCurrentConfigColumnsIndex: [],
      isLoading: false,
    };
  },
  computed: {
    ifSearchTermIsNotEmpty() {
      return this.searchTerm.length ? true : false;
    },
  },
  watch: {
    searchTerm(keyword) {
      this.filteredCurrentConfigColumnsIndex = Object.keys(
        this.currentConfigColumns
      ).filter((i) => i.toLowerCase().indexOf(keyword.toLowerCase()) > -1);
    },
  },
  created() {
    this.configuration["Case Aging"].isVisible = true;
  },
  destroyed() {
    this.configuration["Case Aging"].isVisible = false;
  },
  mounted() {
    console.log("this.configuration", this.configuration);
    const columnsCached = localStorage.getItem(
      `${this.cacheName}_for_export_report`
    );
    let previousConfig = columnsCached ? JSON.parse(columnsCached) : null;
    const currentConfigColumns = previousConfig
      ? previousConfig
      : JSON.parse(JSON.stringify(this.configuration));
    this.currentConfigColumns = currentConfigColumns;
    this.filteredCurrentConfigColumnsIndex = Object.keys(currentConfigColumns);
  },
  methods: {
    onClose() {
      this.$emit("onModalClose");
      this.searchTerm = "";
    },
    onSearch(e) {
      this.searchTerm = e.target.value;
    },
    onClear() {
      this.searchTerm = "";
    },
    toggleColumnVisibility(item) {
      item.isVisible = !item.isVisible;
    },
    saveColumnConfig() {
      const newConfiguration = JSON.parse(
        JSON.stringify(this.currentConfigColumns)
      );
      const columnsToExport = [];
      const columnExportConfiguration = [];
      Object.keys(newConfiguration).forEach((itemKey) => {
        if (newConfiguration[itemKey].isVisible) {
          columnsToExport.push(newConfiguration[itemKey].fieldName);
          columnExportConfiguration.push({
            label: itemKey,
            value: newConfiguration[itemKey].fieldName,
          });
        }
      });
      if (columnsToExport.length) {
        localStorage.setItem(
          `${this.cacheName}_for_export_report`,
          JSON.stringify(newConfiguration)
        );
      } else {
        showToast("warning", "Please select atleast one columns to export");
      }
      this.fetchAllDisputesForExport(columnExportConfiguration);
    },
    restoreDefaults() {
      Object.keys(this.currentConfigColumns).forEach((item) => {
        this.currentConfigColumns[item].isVisible =
          this.defaultColumns.indexOf(item) > -1;
      });
    },
    async fetchAllDisputesForExport(columnExportConfiguration) {
      this.isLoading = true;
      const filters = filterGroupFlatten(this.filterData);
      const dataFetchMaxLimit = 5;
      const respData = [];

      let searchRequestPayload = disputeActions.getDisputeList(
        {
          params: {
            page_no: this.pagination.activePage - 1,
            page_size: 1000,
          },
          data: {
            object_id: "disputes-view",
            filters,
            search: this.appliedSearchTerm,
            ...(this.sortOrderConfiguration.sortBy && {
              sort_by: this.sortOrderConfiguration.sortBy,
              sort_order: this.sortOrderConfiguration.sortOrder,
            }),
          },
        },
        await getMsalIdToken(this)
      );
      for (
        let dataFetchCount = 0;
        dataFetchCount < dataFetchMaxLimit;
        dataFetchCount++
      ) {
        searchRequestPayload.params.page_no = dataFetchCount;
        Vue.$toast.clear();
        showToast(
          "info",
          `Generating report file (${
            dataFetchCount + 1
          }/${dataFetchMaxLimit})...`
        );
        await AxiosFetch(searchRequestPayload)
          .then((response) => {
            if (response.status === 204) {
              showToast("warning", "No data found for applied filters");
              return [];
            } else {
              let resp = (response.data.search_records || []).map((item) => ({
                ...item,
                ...getCaseAging(item),
                // ...getRejectVoiceCaseAging(item),
                // ...getInprocessCaseAging(item),
              }));
              resp.forEach((item) => {
                const itemKeys = Object.keys(item);
                itemKeys.forEach((key) => {
                  if (
                    key.indexOf("Ts") > -1 ||
                    (key.indexOf("Date") > -1 && item[key])
                  ) {
                    item[key] = format(
                      new Date(item[key]),
                      "dd-MMM-yyyy hh:mm a"
                    );
                  } else if (typeof item[key] === "boolean") {
                    item[key] = item[key] ? "Yes" : "No";
                  }
                });
                respData.push(item);
              });
              if (!resp.length || respData.length >= this.recordsCount) {
                dataFetchCount = dataFetchMaxLimit;
              }
              return resp;
            }
          })
          .catch((error) => {
            showToast(
              "error",
              "Unable to download report file, try again after sometime."
            );
            console.error(error);
          });
      }
      this.isLoading = false;
      let settings = {
        fileName: "dispute_records", // Name of the spreadsheet
        extraLength: 3,
        writeOptions: {},
      };
      let data = [
        {
          sheet: "Sheet 1",
          columns: columnExportConfiguration,
          content: respData,
        },
      ];
      xlsx(data, settings);
      Vue.$toast.clear();
      showToast("success", "Report has been generated successfully...");
      setTimeout(() => {
        Vue.$toast.clear();
      }, 5000);
    },
  },
};
</script>

<style lang="scss">
.configure-export-view {
  & .modal--small {
    width: 648px;
    height: 700px;
    & .modal__content--padding {
      padding: 0;
    }
    & .modal__footer__buttons__left--underline {
      color: var(--invert-color);
    }
  }
  &__modal {
    height: 540px;
    display: flex;
    flex-direction: column;
    &__body {
      display: flex;
      justify-content: space-between;
      overflow-y: auto;
      padding: 1.5rem;
      padding-top: 0;
      &__left {
        display: flex;
        flex-direction: column;
        box-shadow: 0px 3px 5px -1px var(--black-variant-1),
          0px 1px 18px var(--black-variant-2),
          0px 6px 10px var(--black-variant-1);
        border-radius: 4px;
        background-color: var(--f-card-bg-color);
        max-width: 12rem;
        width: 100%;
        overflow-y: auto;
        cursor: default;
        @media (max-width: 1024px) {
          display: none;
        }
        &__header {
          font-family: "Maersk Text Bold";
          font-size: 14px;
          line-height: 16px;
          color: var(--mx-date-picker-calendar-color);
          font-weight: 700;
          padding-left: 24px;
          padding-top: 24px;
        }
        &__title {
          font-family: "Maersk Text";
          font-size: 14px;
          line-height: 16px;
          color: var(--mx-date-picker-calendar-color);
          &__tab {
            box-sizing: border-box;
            padding: 12px 24px;
            white-space: nowrap;
          }
        }
      }
      &__right {
        background-color: var(--f-card-bg-color);
        overflow-y: auto;
        box-shadow: 0px 3px 5px -1px var(--black-variant-1),
          0px 1px 18px var(--black-variant-2),
          0px 6px 10px var(--black-variant-1);
        border-radius: 4px;
        width: 100%;
        @media (min-width: 1025px) {
          margin-left: 1rem;
        }
        &__header {
          font-family: "Maersk Text Bold";
          font-size: 14px;
          line-height: 16px;
          color: var(--mx-date-picker-calendar-color);
          font-weight: 700;
          padding-left: 24px;
          padding-top: 24px;
        }
        &__search {
          margin: 12px 22px 0px 26px;
          & .form-input__field {
            background-color: var(--f-card-form-input-color);
          }
        }
        &__title {
          display: flex;
          flex-direction: column;
          &__no-items-found {
            display: flex;
            justify-content: center;
            margin-top: 1rem;
            padding: 1rem 2rem;
          }
          &__column {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border: 1px solid var(--Gray-400);
            box-sizing: border-box;
            box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
            margin: 12px 22px 0px 26px;
            padding: 8px 16px;
            &:last-child {
              margin-bottom: 15px;
            }
            &__icons {
              cursor: pointer;
              display: flex;
              align-content: center;
              & span:first-child {
                margin-right: 0.5rem;
              }
              &__toggle-lock {
                &:focus {
                  outline: none;
                }
              }
              &__disabled {
                cursor: not-allowed;
              }
            }
            &__title {
              font-family: "Maersk Text";
              font-size: 14px;
              color: var(--mx-date-picker-calendar-color);
              cursor: pointer;
              display: flex;
              align-items: center;
              span {
                margin-right: 0.5rem;
              }
            }
          }
        }
      }
    }
    &__notification {
      padding: 0 1.5rem;
    }
  }
}
</style>
